// client/src/pages/SignupStepThree.js - Updated for New Plan Structure
import React, { useState } from 'react';
import { apiUrl } from '../utils/apiBase';
import GradientButton from '../components/GradientButton';

const SignupStepThree = () => {
  const [commPref, setCommPref] = useState('both');
  const [plan, setPlan] = useState('jackpotChaser');
  const [jackpotThreshold, setJackpotThreshold] = useState(500); // Default for Jackpot Chaser
  const [isProcessing, setIsProcessing] = useState(false);

  // URLs
  const STRIPE_URL = process.env.REACT_APP_STRIPE_CHECKOUT_URL || apiUrl('createCheckoutSession');

  // Threshold options based on plan
  const jackpotChaserThresholds = [
    { value: 500, label: '$500 Million+' },
    { value: 750, label: '$750 Million+' },
    { value: 1000, label: '$1 Billion+' }
  ];

  const weeklyPlayerThresholds = [
    { value: 0, label: 'All Drawings (no threshold)' },
    { value: 100, label: '$100 Million+' },
    { value: 200, label: '$200 Million+' },
    { value: 300, label: '$300 Million+' },
    { value: 400, label: '$400 Million+' },
    { value: 500, label: '$500 Million+' }
  ];

  const handlePlanChange = (newPlan) => {
    setPlan(newPlan);
    // Set default threshold for the new plan
    if (newPlan === 'jackpotChaser') {
      setJackpotThreshold(500);
    } else {
      setJackpotThreshold(100); // Weekly Player default
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    console.log('🚀 PAYMENT-FIRST: Starting checkout...');
    
    const rawData = sessionStorage.getItem('lottoSignupData');
    const existingData = JSON.parse(rawData || '{}');

    if (!existingData.firstName || !existingData.email || !existingData.password) {
      alert('Missing required signup information.');
      setIsProcessing(false);
      return;
    }

    const fullData = {
      ...existingData,
      commPref,
      plan,
      jackpotThreshold: String(jackpotThreshold), // Backend expects string in metadata
      termsAcceptedAt: new Date().toISOString(),
      termsSnapshot: "User accepted terms on signup step three."
    };

    try {
      console.log('💳 Creating Stripe checkout session (account will be created after payment)...');
      
      const stripePayload = {
        email: fullData.email,
        firstName: fullData.firstName,
        lastName: fullData.lastName,
        phone: fullData.phone,
        zip: fullData.zip,
        city: fullData.city,
        state: fullData.state,
        plan: fullData.plan,
        jackpotThreshold: fullData.jackpotThreshold,
        commPref: fullData.commPref,
        termsAcceptedAt: fullData.termsAcceptedAt,
        userIP: sessionStorage.getItem('userIP') || 'unknown',
        // Password is sent ONCE over HTTPS so the backend can pre-create the
        // (disabled) Firebase Auth account — it is never stored at Stripe.
        password: fullData.password,
        userAgent: sessionStorage.getItem('userAgent') || 'unknown'
      };

      // Do NOT log stripePayload — it contains the password.
      console.log('📤 Sending checkout data for:', stripePayload.email);

      const stripeRes = await fetch(STRIPE_URL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(stripePayload)
      });

      const stripeData = await stripeRes.json();

      console.log('📥 Stripe response:', stripeData);

      if (!stripeRes.ok || !stripeData.url) {
        console.error('❌ Stripe checkout failed:', stripeData);
        alert(stripeData.error || 'Failed to start payment process. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Scrub the password from browser storage before leaving for Stripe —
      // the backend already has what it needs (disabled account created).
      try {
        sessionStorage.removeItem('signupPassword');
        const scrubbed = { ...existingData, plan };
        delete scrubbed.password;
        delete scrubbed.confirmPassword;
        sessionStorage.setItem('lottoSignupData', JSON.stringify(scrubbed));
      } catch {}

      console.log('✅ Redirecting to Stripe - Account will be created after payment');
      window.location.href = stripeData.url;

    } catch (err) {
      console.error('💥 CRITICAL ERROR:', err);
      alert('Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  };

  const currentThresholds = plan === 'jackpotChaser' ? jackpotChaserThresholds : weeklyPlayerThresholds;

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem'
    }}>
      <div style={{
        fontFamily: 'Arial, sans-serif',
        maxWidth: '450px',
        width: '100%',
        padding: '2rem',
        backgroundColor: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e5e7eb'
      }}>
        <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Step 3: Choose Your Plan</h2>

        <form style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Plan Selection */}
          <div>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.75rem' }}>Select Your Plan:</label>
            
            {/* Jackpot Chaser */}
            <div 
              onClick={() => handlePlanChange('jackpotChaser')}
              style={{
                padding: '1rem',
                marginBottom: '0.75rem',
                border: plan === 'jackpotChaser' ? '2px solid #A6CE3A' : '2px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: plan === 'jackpotChaser' ? '#f0fdf4' : '#fff',
                cursor: 'pointer'
              }}
            >
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  value="jackpotChaser" 
                  checked={plan === 'jackpotChaser'} 
                  onChange={() => handlePlanChange('jackpotChaser')}
                  style={{ marginTop: '4px', marginRight: '10px' }}
                />
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>The Jackpot Chaser</div>
                  <div style={{ color: '#A6CE3A', fontWeight: 'bold', fontSize: '1.25rem' }}>$14.99/year</div>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                    Get numbers only when jackpots reach your threshold. Perfect for big-game players!
                  </div>
                </div>
              </label>
            </div>

            {/* Weekly Player */}
            <div 
              onClick={() => handlePlanChange('weeklyPlayer')}
              style={{
                padding: '1rem',
                border: plan === 'weeklyPlayer' ? '2px solid #A6CE3A' : '2px solid #e5e7eb',
                borderRadius: '12px',
                backgroundColor: plan === 'weeklyPlayer' ? '#f0fdf4' : '#fff',
                cursor: 'pointer'
              }}
            >
              <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  value="weeklyPlayer" 
                  checked={plan === 'weeklyPlayer'} 
                  onChange={() => handlePlanChange('weeklyPlayer')}
                  style={{ marginTop: '4px', marginRight: '10px' }}
                />
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>The Weekly Player</div>
                  <div style={{ color: '#A6CE3A', fontWeight: 'bold', fontSize: '1.25rem' }}>$34.99/year</div>
                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                    Get numbers for every drawing or set your own threshold. Maximum flexibility!
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Jackpot Threshold */}
          <div>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
              {plan === 'jackpotChaser' 
                ? 'Send me numbers when the jackpot is:' 
                : 'When do you want numbers?'}
            </label>
            {currentThresholds.map(({ value, label }) => (
              <label key={value} style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input 
                  type="radio" 
                  value={value} 
                  checked={jackpotThreshold === value} 
                  onChange={() => setJackpotThreshold(value)}
                  style={{ marginRight: '8px' }}
                />
                {label}
              </label>
            ))}
          </div>

          {/* Communication Preference */}
          <div>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>How would you like to receive your numbers?</label>
            <label style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
              <input type="radio" value="text" checked={commPref === 'text'} onChange={() => setCommPref('text')} style={{ marginRight: '8px' }} /> 
              Text
            </label>
            <label style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
              <input type="radio" value="email" checked={commPref === 'email'} onChange={() => setCommPref('email')} style={{ marginRight: '8px' }} /> 
              Email
            </label>
            <label style={{ display: 'block', marginBottom: '0.5rem', cursor: 'pointer' }}>
              <input type="radio" value="both" checked={commPref === 'both'} onChange={() => setCommPref('both')} style={{ marginRight: '8px' }} /> 
              Both
            </label>
          </div>

          {/* Customization Info Message */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            padding: '1rem',
            fontSize: '0.9rem',
            color: '#0369a1'
          }}>
            <strong>💡 Good to know:</strong> You can customize which games, draw days, and jackpot thresholds you want in your profile after signing up.
          </div>

          {/* Payment Button */}
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <GradientButton
              variant="green"
              type="button"
              onClick={handleCheckout}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : `Pay $${plan === 'jackpotChaser' ? '14.99' : '34.99'} →`}
            </GradientButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupStepThree;